import React, { type ReactElement, useCallback, useMemo, useState } from 'react';
import type { RowSelectionState } from '@tanstack/react-table';
import clsx from 'clsx';
import { toInteger } from 'lodash';
import { toast } from 'react-toastify';
import FormButtonsContainer from 'atom-components/form-buttons-container';
import CancelButton from 'atom-components/form-fields-for-hook-form/cancel-button';
import { pluralise } from 'helpers/string.helpers';
import { refreshMarkingTables } from 'pages/marking/helpers/react-query.helpers';
import AllocationResultsModal from 'pages/marking/subpages/marking-allocation-tab/allocation-results-modal';
import type { AllocationResults } from 'pages/marking/subpages/marking-allocation-tab/types';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import { usePostTestInstanceResponseMarkers } from 'services/assist-api/entities/test-instance-response-marker/hooks';
import type { BaseMarkingTask } from 'services/assist-api/entities/test-instance-response-marker/marking-tasks/types';
import type { PostTestInstanceResponseMarker } from 'services/assist-api/entities/test-instance-response-marker/types';
import AllocateResponsesTable from './allocate-responses-table';
import {
  successfullyAllocatedMarkers,
  unsuccessfullyAllocatedMarkers
} from './allocate-responses.helpers';

type Props = {
  selectedResponseIds: number[];
  organisationUsers: BaseCustomerUser[];
  markingTasks: BaseMarkingTask[];
  onClose: () => void;
  onRequestRefetch: () => void;
};

export default function AllocateResponsesTableContainer({
  selectedResponseIds,
  organisationUsers,
  markingTasks,
  onClose,
  onRequestRefetch
}: Props): ReactElement {
  const [showAllocationResultsModal, setShowAllocationResultsModal] = useState<boolean>(false);
  const [allocationResults, setAllocationResults] = useState<AllocationResults | undefined>(
    undefined
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const afterAllocateMarkers = useCallback(
    (data) => {
      const rejectedResponses = data.filter((d) => d.status === 'rejected');
      const fulfilledResponses = data.filter((d) => d.status === 'fulfilled');

      const fulfilled = data.length - rejectedResponses.length;
      const rejectedDueToResponseData = rejectedResponses
        .map((d, index) => ({ ...d, index }))
        .filter((d: PromiseRejectedResult) => {
          const { reason } = d;
          return (
            reason.response.status === 422 &&
            reason.response.data.violations?.filter(({ propertyPath }) => propertyPath === 'user')
              .length === reason.response.data.violations?.length
          );
        });

      if (fulfilled) {
        toast.success(`${fulfilled} / ${data.length} responses allocated.`);
      }

      if (rejectedResponses.length - rejectedDueToResponseData.length) {
        toast.error(
          `${rejectedResponses.length - rejectedDueToResponseData.length} / ${
            data.length
          } responses failed to be allocated.`
        );
      }

      const allocatedMarkers = successfullyAllocatedMarkers(organisationUsers, fulfilledResponses);
      const unallocatedMarkers = unsuccessfullyAllocatedMarkers(
        organisationUsers,
        rejectedResponses
      );
      setAllocationResults({
        allocatedMarkers,
        unallocatedMarkers
      });
      setShowAllocationResultsModal(true);
    },
    [organisationUsers]
  );

  const postTestInstanceResponseMarkers = usePostTestInstanceResponseMarkers({
    onSuccess: (data: PromiseRejectedResult[]) => {
      const rejected = data.filter((d) => d.status === 'rejected').length;
      const fulfilled = data.length - rejected;
      if (rejected) {
        toast.error(`Failed to allocate response to ${rejected} ${pluralise('marker', rejected)}.`);
      }
      if (fulfilled) {
        toast.success(`Allocated response to ${fulfilled} ${pluralise('marker', fulfilled)}.`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to allocate response to markers: ${error.message}`);
    }
  });

  const createTestInstanceResponseMarkers = useCallback(
    async (responseIds: number[], organisationUserIds: number[]) => {
      const markersToPost: PostTestInstanceResponseMarker[] = [];
      responseIds.forEach((responseId) => {
        organisationUserIds.forEach((organisationUserId) => {
          markersToPost.push({
            testInstanceResponseId: responseId,
            customerUserId: organisationUserId
          });
        });
      });
      await postTestInstanceResponseMarkers.mutateAsync(markersToPost, {
        onSuccess: (data) => {
          afterAllocateMarkers(data);
        }
      });
    },
    [afterAllocateMarkers, postTestInstanceResponseMarkers]
  );

  // Disable the Allocate Responses button if no rows are selected:
  const allocateResponsesButtonDisabled = useMemo(
    () => Object.keys(rowSelection).length === 0,
    [rowSelection]
  );

  const selectedOrganisationUserIds = useMemo(
    () => Object.keys(rowSelection).map((id) => toInteger(id)),
    [rowSelection]
  );

  return (
    <>
      {showAllocationResultsModal && allocationResults && (
        <AllocationResultsModal
          isOpen={showAllocationResultsModal}
          onClose={(): void => {
            setShowAllocationResultsModal(false);

            // Clear the queries used to populate the Marking tables so that these will contain the
            // new allocations:
            refreshMarkingTables();
            onRequestRefetch();

            // Automatically close the Allocate Responses modal too, rather than requiring the user
            // to manually close both modals:
            onClose();
          }}
          allocationResults={allocationResults}
        />
      )}
      <div>
        <AllocateResponsesTable
          selectedResponseIds={selectedResponseIds}
          organisationUsers={organisationUsers}
          markingTasks={markingTasks}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
        />
        <FormButtonsContainer>
          <CancelButton onClick={onClose} />
          <button
            type="button"
            className={clsx(`btn btn-primary`, {
              disabled: allocateResponsesButtonDisabled
            })}
            disabled={allocateResponsesButtonDisabled}
            onClick={(): Promise<void> =>
              createTestInstanceResponseMarkers(selectedResponseIds, selectedOrganisationUserIds)
            }
          >
            Allocate Responses
          </button>
        </FormButtonsContainer>
      </div>
    </>
  );
}
